import { reportError } from '../../monitoring/errorMonitor';
import cs from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { tryCatch } from '@wix/funnel-utils';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { usePreviewTemplate } from '../../contexts/PreviewTemplateContext';
import { useInitialState } from '../../contexts/InitialStateContext';
import { useFlow, STUDIO_MOBILE_FLOW } from '../../hooks/useFlow';
import { createMetaSiteByTemplate } from '../../api/createMetaSiteByTemplate';
import { PreviewHeader } from './PreviewHeader/PreviewHeader';
import { ErrorBanner } from './ErrorBanner/ErrorBanner';
import { TemplateSaved } from './TemplateSaved/TemplateSaved';
import { TemplateDemoPreview } from './TemplateDemoPreview/TemplateDemoPreview';
import { templatePreviewModalDataHooks } from './TemplatePreviewModal.dataHooks';
import s from './TemplatePreviewModal.scss';

const POST_PROVISION_REDIRECT_URL =
  'https://wix.com/intro/studio-mobile?ref=studio_save_template';

export const TemplatePreviewModal: FC = () => {
  const {
    previewTemplate,
    setIsSavedTemplate,
    setPreviewTemplate,
    isSavedTemplate,
  } = usePreviewTemplate();
  const biLogger = useBiLogger();
  const flow = useFlow();
  const { baseUrl, userData } = useInitialState();
  const [isAppear, setIsAppear] = useState(false);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [hasProvisionError, setHasProvisionError] = useState(false);

  const isStudioMobileFlow = flow === STUDIO_MOBILE_FLOW;

  useEffect(() => {
    if (previewTemplate) {
      setIsAppear(true);
      document.querySelector('body')!.className = s.rmScroll;

      biLogger.reportViewTemplate({
        templateId: previewTemplate.templateId,
        slug: previewTemplate.slug,
      });
    }

    return () => {
      setIsAppear(false);
      document.querySelector('body')!.className = '';
    };
  }, [previewTemplate]);

  const provisionSite = async () => {
    if (!previewTemplate) {
      return;
    }

    biLogger.reportGeneralAction({
      action: 'save_template',
      templateId: previewTemplate.templateId,
    });

    setHasProvisionError(false);
    setIsProvisioning(true);

    const [error, metaSiteId] = await tryCatch(() =>
      createMetaSiteByTemplate(baseUrl, previewTemplate.metaSiteId),
    );

    if (error || !metaSiteId) {
      reportError(error || new Error('Site creation returned no metaSiteId'), {
        operation: 'create-metasite',
      });
      setIsProvisioning(false);
      setHasProvisionError(true);
      return;
    }

    const redirectUrl = new URL(POST_PROVISION_REDIRECT_URL);
    redirectUrl.searchParams.set('metaSiteId', metaSiteId);
    window.location.assign(redirectUrl.toString());
  };

  const onSaveTemplateButtonClick = () => {
    biLogger.reportGeneralAction({
      action: 'save_template',
      templateId: previewTemplate?.templateId,
    });
    setIsSavedTemplate(true);
  };

  const onCloseButtonClick = () => {
    biLogger.reportGeneralAction({
      action: 'close_template_saved',
      templateId: previewTemplate?.templateId,
    });
    setIsSavedTemplate(false);
  };

  const onBrowseMoreTemplatesButtonClick = () => {
    biLogger.reportGeneralAction({
      action: 'browse_more_templates',
      templateId: previewTemplate?.templateId,
    });
    setPreviewTemplate(null);
    setIsSavedTemplate(false);
  };

  if (!previewTemplate) {
    return null;
  }

  return (
    <div
      data-hook={templatePreviewModalDataHooks.container()}
      className={cs(s.templatePreviewModal, {
        [s.appear]: isAppear,
      })}
    >
      {isSavedTemplate ? (
        <TemplateSaved
          onCloseClick={onCloseButtonClick}
          onButtonClick={onBrowseMoreTemplatesButtonClick}
          savedTemplate={previewTemplate}
        />
      ) : (
        <div className={s.previewBox}>
          <PreviewHeader
            onButtonClick={
              isStudioMobileFlow && userData.isLoggedIn
                ? provisionSite
                : onSaveTemplateButtonClick
            }
            isLoading={isProvisioning}
            isStudioMobileFlow={isStudioMobileFlow}
          />
          <TemplateDemoPreview template={previewTemplate} />
          {hasProvisionError && (
            <ErrorBanner
              onTryAgainClick={provisionSite}
              onCloseClick={() => setHasProvisionError(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};
